import {useEffect, useRef} from 'react';

import type {RefObject} from 'react';

/** A damped spring retains velocity when a second selection interrupts the first. */
export const useCinemaSpring = (target: number, count: number): RefObject<HTMLDivElement | null> => {
  const stage = useRef<HTMLDivElement>(null);
  const motion = useRef({position: target, velocity: 0});

  useEffect(() => {
    const element = stage.current;
    if(!element) {
      return undefined;
    }
    const preference = window.matchMedia('(prefers-reduced-motion: reduce)');
    const cards = Array.from(element.querySelectorAll<HTMLElement>('[data-cinema-card]'));
    let frame = 0;
    let last = 0;
    const paint = (): void => {
      cards.forEach((card, index) => {
        const offset = ((((index - motion.current.position + (count / 2)) % count) + count) % count) - (count / 2);
        const distance = Math.abs(offset);
        const depth = Math.min(distance, 1.8);
        card.style.transform = `translateX(-50%) translateX(${offset * 90}%) translateZ(${-depth * 180}px) rotateY(${-offset * 42}deg) rotateZ(${-offset * 1.5}deg) scale(${1 - (depth * 0.12)}) translateY(var(--float-offset, 0px)) rotateX(var(--float-x, 0deg)) rotateY(var(--float-y, 0deg)) rotateZ(var(--float-z, 0deg))`;
        card.style.opacity = String(Math.max(0, 1 - (Math.max(0, distance - 1) * 2.5)));
        card.style.filter = `brightness(${1 - (Math.min(distance, 1) * 0.36)})`;
        card.style.zIndex = String(Math.round(100 - (distance * 30)));
      });
    };
    const tick = (time: number): void => {
      const elapsed = last ? Math.min((time - last) / 1000, 0.032) : 1 / 60;
      last = time;
      // Small substeps keep the mass/spring stable even on slower displays.
      const steps = 4;
      const dt = elapsed / steps;
      for(let index = 0; index < steps; index += 1) {
        const acceleration = ((target - motion.current.position) * 150) - (motion.current.velocity * 20);
        motion.current.velocity += acceleration * dt;
        motion.current.position += motion.current.velocity * dt;
      }
      const settled = Math.abs(target - motion.current.position) < 0.0005 && Math.abs(motion.current.velocity) < 0.005;
      if(settled) {
        motion.current = {position: target, velocity: 0};
      }
      paint();
      if(!settled) {
        frame = requestAnimationFrame(tick);
      }
    };
    const start = (): void => {
      cancelAnimationFrame(frame);
      last = 0;
      if(preference.matches) {
        motion.current = {position: target, velocity: 0};
        paint();
      } else {
        paint();
        frame = requestAnimationFrame(tick);
      }
    };
    start();
    preference.addEventListener('change', start);
    return () => {
      cancelAnimationFrame(frame);
      preference.removeEventListener('change', start);
    };
  }, [count, target]);

  return stage;
};
